import { FC, useMemo, useRef } from 'react';

import { Close16, WarningFill } from 'ui/icons';
import {
  ImageHeader,
  MessageMedium,
  MetaApprovalStatusDefinition,
  WhatsAppContentDefinition,
} from 'modules/graphql/types';
import { EmbeddedMessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { Button, IconButton, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Drawer, DrawerHeader, DrawerContent, useDrawerContext } from 'ui/fullScreen';
import { NoOp } from 'utils/noop';

import { SettingsForm as EditEmailSettingsForm } from '../EditEmail/SettingsForm';
import { SettingsForm as EditInAppSettingsForm } from '../EditInApp/SettingsForm';
import { SettingsForm as EditPushSettingsForm } from '../EditPush/SettingsForm';
import { SettingsForm as EditSMSSettingsForm } from '../EditSMS/SettingsForm';
import { SettingsForm as EditWebPushSettingsForm } from '../EditWebPush/SettingsForm';
import { SettingsForm as EditEmbeddedSettingsForm } from '../EditEmbedded/settings/SettingsForm';
import { SettingsForm as EditWhatsAppSettingsForm } from '../EditWhatsApp/SettingsForm';

import { displayContent } from './content';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { SelectedMessageMedium, UnselectedMessageMedium } from './types';
import { StyledDrawerFooter, UnsavedChangesMessage } from './EditorContentContainer.styled';
import { useSaveTemplate } from '../hooks/useSaveTemplate';

const SettingsComponent: Record<SelectedMessageMedium, FC<React.PropsWithChildren<unknown>>> = {
  [MessageMedium.Email]: EditEmailSettingsForm,
  [MessageMedium.InApp]: EditInAppSettingsForm,
  [MessageMedium.Push]: EditPushSettingsForm,
  [MessageMedium.SMS]: EditSMSSettingsForm,
  [MessageMedium.WebPush]: EditWebPushSettingsForm,
  [EmbeddedMessageMedium.Embedded]: EditEmbeddedSettingsForm,
  [WhatsAppMedium.WhatsApp]: EditWhatsAppSettingsForm,
  [UnselectedMessageMedium.Unselected]: () => null,
};

type Props = {
  onSaveSuccess?: NoOp;
};

export const SettingsDrawer = ({ onSaveSuccess }: Props) => {
  const { saveTemplate } = useSaveTemplate();
  const { isDirty, liveTemplate } = useEditableFieldsContext();
  const { mediumData } = liveTemplate;
  const { medium } = useTemplateQueryContext();
  const { isDrawerOpen, toggleDrawer } = useDrawerContext();
  const SettingsDrawerComponent = SettingsComponent[medium] || (() => null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleCloseOnDrawer = () => {
    toggleDrawer(false);
  };

  const handleSaveDetailButton = () => {
    saveTemplate(onSaveSuccess);
  };

  const isDetailButtonHidden = useMemo(() => {
    if (medium === WhatsAppMedium.WhatsApp) {
      const whatsAppData = mediumData as WhatsAppContentDefinition;
      if (whatsAppData.metaApprovalStatus === MetaApprovalStatusDefinition.Pending) {
        return true;
      }
      if (
        !isDirty &&
        whatsAppData.metaApprovalStatus === MetaApprovalStatusDefinition.Approved &&
        !(whatsAppData?.header as ImageHeader)?.mediaUrl
      ) {
        return true;
      }
    }
    return false;
  }, [isDirty, medium, mediumData]);

  return (
    <Drawer ref={drawerRef}>
      <DrawerHeader data-test="drawer-header">
        <span>{displayContent.drawerTitle}</span>
        <IconButton onClick={handleCloseOnDrawer} variant={BUTTON_VARIANTS.GHOST}>
          <Close16 />
        </IconButton>
      </DrawerHeader>
      <DrawerContent data-test="drawer-content">
        {isDrawerOpen && <SettingsDrawerComponent />}
      </DrawerContent>
      <StyledDrawerFooter data-test="drawer-footer">
        {isDetailButtonHidden ? (
          <Button
            data-test={'template-editor-close-button'}
            onClick={handleCloseOnDrawer}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            <span>{displayContent.btnClose}</span>
          </Button>
        ) : (
          <Button
            isDisabled={!isDirty}
            data-test={'template-editor-details-button'}
            onClick={handleSaveDetailButton}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            <span>{displayContent.btnSaveSettings}</span>
          </Button>
        )}
        {isDirty && (
          <UnsavedChangesMessage data-test={'unsaved-changes-message'}>
            <WarningFill isColorInherited={true} />
            {displayContent.strUnsavedChanges}
          </UnsavedChangesMessage>
        )}
      </StyledDrawerFooter>
    </Drawer>
  );
};
